import { useRef, useState } from "react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNotify } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { parseHofItsList } from "./parse-hof-its-list";

type PreviewMatched = {
  inputItsNo: string;
  hofItsNo: string;
  hofName: string | null;
  resolvedFromFm: boolean;
};

type PreviewAlready = {
  inputItsNo: string;
  hofItsNo: string;
  hofName: string | null;
};

type PreviewResult = {
  capacity: number;
  currentCount: number;
  remainingCapacity: number;
  wouldExceedCapacity: boolean;
  matched: PreviewMatched[];
  notFound: string[];
  alreadyOnSlot: PreviewAlready[];
  duplicatesInList: string[];
};

type Step = "input" | "review";

type Props = {
  open: boolean;
  onClose: () => void;
  slotId: string;
  onAdded: () => void | Promise<void>;
};

export function UploadHofListDialog({ open, onClose, slotId, onAdded }: Props) {
  const notify = useNotify();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>("input");
  const [rawText, setRawText] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [includeUnknown, setIncludeUnknown] = useState(false);

  const reset = () => {
    setStep("input");
    setRawText("");
    setPreview(null);
    setIncludeUnknown(false);
    setPreviewing(false);
    setAdding(false);
  };

  const handleClose = () => {
    if (previewing || adding) return;
    reset();
    onClose();
  };

  const onFilePicked = async (file: File | null) => {
    if (!file) return;
    const text = await file.text();
    setRawText(text);
  };

  const runPreview = async () => {
    const itsList = parseHofItsList(rawText);
    if (itsList.length === 0) {
      notify("Paste or upload at least one ITS number", { type: "warning" });
      return;
    }
    setPreviewing(true);
    try {
      const res = await callApi({
        location: "houseVisitStops",
        method: "POST",
        id: "preview-add",
        data: { slotId, hofItsNos: itsList },
      });
      setPreview(res.data as PreviewResult);
      setStep("review");
      setIncludeUnknown(false);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to preview HOF list";
      notify(message, { type: "warning" });
    } finally {
      setPreviewing(false);
    }
  };

  const itsToAdd = (): string[] => {
    if (!preview) return [];
    const matched = preview.matched.map((m) => m.hofItsNo);
    if (!includeUnknown) return matched;
    return [...matched, ...preview.notFound];
  };

  const confirmAdd = async () => {
    const hofItsNos = itsToAdd();
    if (hofItsNos.length === 0) {
      notify("Nothing to add", { type: "warning" });
      return;
    }
    if (preview && hofItsNos.length > preview.remainingCapacity) {
      notify(
        `Only ${preview.remainingCapacity} stop(s) remaining on this slot (capacity ${preview.capacity})`,
        { type: "warning" }
      );
      return;
    }
    setAdding(true);
    try {
      const res = await callApi({
        location: "houseVisitStops",
        method: "POST",
        data: { slotId, hofItsNos },
      });
      const body = res.data as { count?: number };
      const n = body.count ?? hofItsNos.length;
      notify(n === 1 ? "1 stop added" : `${n} stops added`, { type: "info" });
      reset();
      onClose();
      await onAdded();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to add stops";
      notify(message, { type: "warning" });
    } finally {
      setAdding(false);
    }
  };

  const matchedCount = preview?.matched.length ?? 0;
  const notFoundCount = preview?.notFound.length ?? 0;
  const alreadyCount = preview?.alreadyOnSlot.length ?? 0;
  const addCount = matchedCount + (includeUnknown ? notFoundCount : 0);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {step === "input" ? "Upload HOF list" : "Review HOF list"}
      </DialogTitle>
      <DialogContent>
        {step === "input" ? (
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Paste ITS numbers (one per line or comma-separated), or upload a .csv / .txt file.
            </Typography>
            <TextField
              label="ITS numbers"
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              multiline
              minRows={8}
              fullWidth
              placeholder={"50498635\n50412345\n…"}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                hidden
                onChange={(e) => {
                  void onFilePicked(e.target.files?.[0] ?? null);
                  e.target.value = "";
                }}
              />
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                Choose file
              </Button>
              <Typography variant="caption" color="text.secondary">
                {parseHofItsList(rawText).length} ITS detected
              </Typography>
            </Stack>
          </Stack>
        ) : preview ? (
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={`${matchedCount} matched`}
              />
              {notFoundCount > 0 ? (
                <Chip
                  size="small"
                  color="error"
                  variant="outlined"
                  label={`${notFoundCount} not found`}
                />
              ) : null}
              {alreadyCount > 0 ? (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={`${alreadyCount} already on slot`}
                />
              ) : null}
              <Chip
                size="small"
                variant="outlined"
                label={`${preview.remainingCapacity} capacity left`}
              />
            </Stack>

            {notFoundCount > 0 ? (
              <Alert severity="warning">
                <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                  These ITS numbers were not found in the directory
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {preview.notFound.join(", ")}
                </Typography>
                <FormControlLabel
                  sx={{ mt: 1, mb: 0 }}
                  control={
                    <Checkbox
                      checked={includeUnknown}
                      onChange={(e) => setIncludeUnknown(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Also add not-found ITS as stops"
                />
              </Alert>
            ) : null}

            {preview.wouldExceedCapacity || addCount > preview.remainingCapacity ? (
              <Alert severity="error">
                Adding {addCount} would exceed remaining capacity ({preview.remainingCapacity} of{" "}
                {preview.capacity}).
              </Alert>
            ) : null}

            {matchedCount > 0 ? (
              <Box sx={{ maxHeight: 280, overflow: "auto", border: 1, borderColor: "divider" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Input ITS</TableCell>
                      <TableCell>HOF ITS</TableCell>
                      <TableCell>Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.matched.map((row) => (
                      <TableRow key={`${row.inputItsNo}-${row.hofItsNo}`}>
                        <TableCell>
                          {row.inputItsNo}
                          {row.resolvedFromFm ? (
                            <Typography component="span" variant="caption" color="text.secondary">
                              {" "}
                              (FM→HOF)
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>{row.hofItsNo}</TableCell>
                        <TableCell>{row.hofName || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No matched HOFs to add
                {includeUnknown && notFoundCount > 0
                  ? ` — ${notFoundCount} unknown ITS will be added if you continue.`
                  : "."}
              </Typography>
            )}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {step === "review" ? (
          <Button
            onClick={() => {
              setStep("input");
              setPreview(null);
            }}
            disabled={adding}
          >
            Back
          </Button>
        ) : (
          <Button onClick={handleClose} disabled={previewing}>
            Cancel
          </Button>
        )}
        {step === "input" ? (
          <Button
            variant="contained"
            disabled={previewing || !rawText.trim()}
            onClick={() => {
              void runPreview();
            }}
            startIcon={previewing ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ fontWeight: 700 }}
          >
            {previewing ? "Checking…" : "Review"}
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={
              adding || addCount === 0 || Boolean(preview && addCount > preview.remainingCapacity)
            }
            onClick={() => {
              void confirmAdd();
            }}
            startIcon={adding ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ fontWeight: 700 }}
          >
            {adding ? "Adding…" : addCount === 1 ? "Add 1 stop" : `Add ${addCount} stops`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default UploadHofListDialog;
